import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import type { GuestStats } from '@/lib/types'

export interface UseGuestStatsResult {
  stats: GuestStats | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useGuestStats(): UseGuestStatsResult {
  const [stats, setStats] = useState<GuestStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStats = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase.rpc('get_guest_stats')

      if (fetchError) throw fetchError

      // get_guest_stats returns an array with one row, so access the first element
      if (data && data.length > 0 && data[0]) {
        setStats(data[0] as GuestStats)
      } else {
        setStats(null)
      }
    } catch (err) {
      console.error('Error fetching guest stats:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch statistics')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()

    // Set up real-time subscription for guests and additional_guests tables
    // Refetch stats whenever either table changes
    const guestsChannel = supabase
      .channel('guests-stats-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'guests',
        },
        () => {
          console.log('Guests table changed, refetching stats')
          fetchStats()
        }
      )
      .subscribe()

    const additionalGuestsChannel = supabase
      .channel('additional-guests-stats-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'additional_guests',
        },
        () => {
          console.log('Additional guests table changed, refetching stats')
          fetchStats()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(guestsChannel)
      supabase.removeChannel(additionalGuestsChannel)
    }
  }, [])

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  }
}
